import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DetectedOriginal, Original, OriginalSort } from '../../ApiTypes';
import { getOriginals } from '../../api';
import { groupOriginals, matchesOriginalQuery, sortOriginalGroups } from '../../originals';
import { SearchInput } from '../../styles/Inputs';
import { EmptyMessage, LoadingMessage } from '../../styles/Messages';
import { useAsyncEffect } from '../../utils';
import PillTabs from '../PillTabs';
import { Trans, useTranslation } from 'react-i18next';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

// 一度に描く件数。取り込んだ動画は 1,700 件を超える人もいるため、
// 全件を DOM に置くと履歴タブを開いた瞬間に固まる。
const ORIGINAL_PAGE_SIZE = 60;

const MoreButton = styled.button`
  margin-top: 12px;
  align-self: center;
  border: 1px solid #4d4d4d;
  border-radius: 999px;
  background: none;
  color: #d0d0d0;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;

  &:hover {
    border-color: #6d6d6d;
    color: #ffffff;
  }
`;

const OriginalList = styled.div`
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto; 
  gap: 10px;

  @media (max-width: 560px) {
    max-height: 400px
  }
  @media (min-width: 560px) {
    max-height: 550px;
  }
`;

const OriginalItemContainer = styled.div<{ selected: boolean, disabled: boolean }>`
  background-color: ${props => props.selected ? 'rgb(254, 167, 0)' : '#2b2b2b'};
  color: ${props => props.selected ? 'black' : 'white'};
  border-radius: 10px;
  padding: 10px;
  gap: 10px;
  // border: 1px solid #424242;
  display: flex;
  flex-direction: column;
  cursor: ${props => props.disabled ? "default" : "pointer"};
  width: 200px;
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  @media (max-width: 560px) {
    box-sizing: border-box;
    width: calc(50% - 5px);
  }
`;

const OriginalThumbnailImg = styled.img`
  border-radius: 10px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  object-position: center; 
  overflow: hidden;

  @media (max-width: 560px) {
    width: 100%;
    height: auto;
  }
`;

const OriginalDeletedText = styled.div`
  border-radius: 10px;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  font-size: 16px;
  color: darkred;
  font-weight: bold;
  display: flex;
  align-items: center;
  text-align: center;
  background: lightgray;

  @media (max-width: 560px) {
    width: 100%;
    height: auto;
  }
`;

const OriginalTitle = styled.div`
  font-size: 12px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  flex: 1;
  line-height: 16px;
  overflow: hidden;
`;

const OriginalItem: FC<{ original: Original, selected: boolean, onSelect(): void }> = (props) => {
  const [hasLoaded, setHasLoaded] = useState<boolean|null>(null);
  const imgRef = useRef<HTMLImageElement|null>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) {
      return;
    }
    if (img.complete) {
      setHasLoaded(img.naturalHeight > 100);
    } else {
      const onLoad = () => {
        setHasLoaded(img.naturalHeight > 100);
      };
      img.addEventListener("load", onLoad);
      return () => {
        img.removeEventListener("load", onLoad);
      };
    }
  }, [props.original.detected.imageUrl, imgRef.current]);

  return <OriginalItemContainer selected={props.selected} disabled={hasLoaded !== true} onClick={() => props.onSelect()}>
    <OriginalTitle>{props.original.detected.title}</OriginalTitle>
    {hasLoaded !== false && <OriginalThumbnailImg ref={imgRef} src={props.original.detected.imageUrl} />}
    {hasLoaded === false && <OriginalDeletedText><Trans>This video has been deleted.</Trans></OriginalDeletedText>}
  </OriginalItemContainer>;
};

const SORT_OPTIONS: { value: OriginalSort, label: string }[] = [
  { value: "lastUsed", label: "Latest used" },
  { value: "newest", label: "Latest added" },
  { value: "oldest", label: "Oldest added" },
];

const SelectOriginal: FC<{ updationKey: any, original: DetectedOriginal|null, onSelect(original: DetectedOriginal|null): void }> = (props) => {
  const { t } = useTranslation();
  const [originals, setOriginals] = useState<Original[]|null>(null);
  const [sort, setSort] = useState<OriginalSort>("lastUsed");
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(ORIGINAL_PAGE_SIZE);

  useAsyncEffect(async () => {
    setOriginals(await getOriginals());
  }, [props.updationKey]);

  useEffect(() => {
    setVisibleCount(ORIGINAL_PAGE_SIZE);
  }, [search, sort]);

  const groups = originals ? groupOriginals(originals) : [];
  const matched = sortOriginalGroups(groups.filter(group => matchesOriginalQuery(group, search)), sort);
  const page = matched.slice(0, visibleCount);
  // 選択済みの動画（`/library` から指定されて来た場合など）が表示範囲の外だと
  // 「選んだはずのものが見当たらない」状態になるので、先頭に出す。
  const selected = props.original ? groups.find(group => group.url === props.original?.url) : undefined;
  const shown = selected && !page.includes(selected) && search.length === 0
    ? [selected, ...page]
    : page;

  return <Container>
    {originals && groups.length > 0 && <Filters>
      <SearchInput
        value={search}
        placeholder={t("Search by title")}
        onChange={event => setSearch(event.target.value)} />
      <PillTabs value={sort} options={SORT_OPTIONS} onChange={value => setSort(value)} />
    </Filters>}
    {originals && <OriginalList>
      {shown.map(group => <OriginalItem
        key={group.url}
        onSelect={() => props.onSelect(group.original)}
        selected={group.url == props.original?.url}
        original={group.original} />)}
    </OriginalList>}
    {matched.length > page.length && <MoreButton onClick={() => setVisibleCount(visibleCount + ORIGINAL_PAGE_SIZE)}>
      <Trans>Show more</Trans> ({matched.length - page.length})
    </MoreButton>}
    {originals && groups.length > 0 && shown.length == 0 && <EmptyMessage>
      <Trans>No videos matched your search</Trans>
    </EmptyMessage>}
    {originals && groups.length == 0 && <EmptyMessage>
      <Trans>You have not yet imported videos</Trans>
    </EmptyMessage>}
    {!originals && <LoadingMessage />}
  </Container>;
};

export default SelectOriginal;
